import { useLazyQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { isMobile } from 'react-device-detect'

import ReceiptParticipation from '@web/components/Modal/ReceiptParticipation'
import { UnavailableType } from '@web/components/Modal/UnavailableUser/userModal.constant'
import { useUnavailableUserModal } from '@web/components/Modal/UnavailableUser/useUnavailableUserModal'
import { useModal } from '@web/components/Modal/useModal'
import { useNotMobileModal } from '@web/components/Modal/useNotMobileModal'
import VerifyNaverPay from '@web/components/Modal/VerifyNaverPay'
import VerifyRealName from '@web/components/Modal/VerifyRealName'
import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { PHASE } from '@web/libs/constants'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'
import { Nullish } from '@web/types'

import { useGlobalStaticStateContext } from '../useGlobalStaticState'
import { useNavigation } from '../useNavigation'

type ReturnType = {
  validateAccess: () => Promise<Nullish<void>>
}

type Props = {
  // 체크인 권한 검사 통과 이후 실행될 콜백 함수
  onSuccess: () => void
  /** 영수증약관 동의여부 검사 */
  isReceiptCheckin?: boolean
  /** 비 로그인 시 콜백, 있으면 실행, 기본 값은 로그인 페이지로 이동 */
  onNotLoggedIn?: () => void
  /**
   * 모달 닫기시 콜백
   */
  onCloseModal?: () => void
  /**
   * 모달 닫기 시 뒤로가기 여부
   */
  needToGoBack?: boolean
}

export const UseCheckinAccess_MeQuery = gql(`
  query UseCheckinAccess_Me {
    ...UseCheckinAccess_Query
  }
`)

const useCheckinAccess = ({
  onSuccess,
  onNotLoggedIn,
  isReceiptCheckin,
  onCloseModal,
  needToGoBack = false,
}: Props): ReturnType => {
  const { isLogin } = useGlobalStaticStateContext()
  const navigation = useNavigation()
  const router = useRouter()

  const [checkinAccessQuery] = useLazyQuery(UseCheckinAccess_MeQuery, { fetchPolicy: 'cache-first' })

  const { openNotMobileModal } = useNotMobileModal()
  const { openUnavailableUserModal } = useUnavailableUserModal({ isGoBack: needToGoBack, shouldUseCheckinQuery: true })

  const { openModal: openVerifyRealNameModal } = useModal(VerifyRealName, {
    successCallbackUrl: router.pathname,
    callback: {
      cancel: onCloseModal,
    },
  })

  const { openModal: openVerifyNaverPayModal } = useModal(VerifyNaverPay, {
    successCallbackUrl: router.pathname,
    callback: {
      cancel: onCloseModal,
    },
  })

  const { openModal: openReceiptParticipationModal } = useModal(ReceiptParticipation, {
    onSignUp: onSuccess,
    callback: {
      cancel: onCloseModal,
    },
  })

  const validateAccess = async () => {
    if (!isMobile && PHASE !== 'development') return openNotMobileModal()

    if (!isLogin) {
      if (onNotLoggedIn) return onNotLoggedIn()
      return navigation.goToLogin()
    }
    const { data, error, loading } = await checkinAccessQuery()

    if (loading) {
      return openErrorToast('리뷰 쓰기 준비중입니다. 잠시 후 다시 시도해주세요.')
    }

    const { MeSucceed } = extractByTypename(data?.me)

    if (!MeSucceed || error) {
      neloLogger('warn', `권한 체크 중 오류 발생: ${extractErrorMessage(error)}`)
      return openErrorToast('권한 체크중 일시적인 오류가 발생했습니다.')
    }

    const { isRealName, isNPaySigned, isParticipant, isUnavailableCheckin } = MeSucceed.user

    if (isUnavailableCheckin) {
      return void openUnavailableUserModal(UnavailableType.CHECKIN)
    }

    if (!isRealName) {
      return openVerifyRealNameModal()
    }

    if (!isNPaySigned) {
      return openVerifyNaverPayModal()
    }

    if (isReceiptCheckin && !isParticipant) {
      return openReceiptParticipationModal()
    }

    return onSuccess()
  }

  return { validateAccess }
}

useCheckinAccess.fragment = gql(`
  fragment UseCheckinAccess_User on User {
    ...UseUnavailableUserModal_User
    isRealName
    isNPaySigned
    isParticipant
    currentGrade
  }

  fragment UseCheckinAccess_Query on Query {
    me {
      ... on MeSucceed {
        user {
          id
          ...UseCheckinAccess_User
        }
      }
      ... on BaseError {
        message
      }
    }
    # isPayMaintenanceTime
  }
`)

export default useCheckinAccess
